import { Circle, CircleDashed } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface RadiusCirclesToggleProps {
  active: boolean;
  onToggle: () => void;
}

/**
 * `<RadiusCirclesToggle>` — single chip controlling whether the amenity
 * radius rings render on `/app/map`. Default OFF — with 539 amenities and
 * overlapping circles the geometry is visually noisy unless explicitly
 * requested. The classification logic (`covered` boolean for in/out of
 * radius) runs independently of this flag, so HighlightToggle and
 * RadiusOnlyToggle work either way.
 *
 * Active = Circle (filled outline). Inactive = CircleDashed (muted).
 * Tide-400 (light blue) accent matches the default property marker color
 * and visually keys "geometry" rather than "filter".
 *
 * Accessibility: `role="switch"` + `aria-checked`; aria-label is the long
 * form for screen readers.
 */
export function RadiusCirclesToggle({ active, onToggle }: RadiusCirclesToggleProps) {
  const Icon = active ? Circle : CircleDashed;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={active}
      aria-label="Show amenity radius rings on the map"
      onClick={onToggle}
      className={cn(
        'inline-flex h-8 px-3 rounded-full border items-center gap-1.5 text-sm font-semibold cursor-pointer',
        'transition-colors',
        'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2',
        active
          ? 'bg-tide-400/20 border-tide-400/60 text-foam-50'
          : 'bg-ocean-800/40 border-foam-50/10 text-foam-50/60',
      )}
    >
      <Icon className="w-4 h-4 shrink-0" aria-hidden />
      Show radius rings
    </button>
  );
}
